import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Input, Space, Table, Tag } from 'antd';
import { message } from '../../utils/antd-message';
import {
  entitlementBalancesApi,
  type EntitlementBalanceItem,
} from '../../api/entitlementBalances.api';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { periodPassStatusColor } from '../../utils/period-ledger';
import { useGlobalCampusScope } from '../../auth/GlobalCampusContext';
import { BalanceHistoryModal, type BalanceHistoryContext } from './BalanceHistoryModal';

/**
 * ADM-34 剩余课时查询：姓名/SKU 模糊筛选 + 三列课时余额表格
 * （H-3 双池直读：购买=正式课池、赠送=赠送课池、可用=两者之和，服务端排序分页），
 * 点击任意课时数字打开该学生该 SKU 的课时流水明细弹窗。纯查询视图，无操作列。
 */
export function BalanceInquiryPage() {
  const [studentName, setStudentName] = useState('');
  const [skuName, setSkuName] = useState('');
  const [rows, setRows] = useState<EntitlementBalanceItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [historyContext, setHistoryContext] = useState<BalanceHistoryContext | null>(null);
  // 全局校区范围：'ALL' 不携带 campusId（锁定模式）；指定校区随请求收窄（O-27）
  const { scope } = useGlobalCampusScope();

  const pageSize = 20;

  const loadBalances = useCallback(
    async (targetPage: number, filters?: { studentName: string; skuName: string }) => {
      const effective = filters ?? { studentName, skuName };
      setLoading(true);
      try {
        const res = await entitlementBalancesApi.listBalances({
          campusId: scope === 'ALL' ? undefined : scope,
          studentName: effective.studentName.trim() || undefined,
          skuName: effective.skuName.trim() || undefined,
          page: targetPage,
          pageSize,
        });
        setRows(res.items);
        setTotal(res.total);
        setPage(targetPage);
      } catch {
        message.error('加载剩余课时失败');
      } finally {
        setLoading(false);
      }
    },
    [studentName, skuName, scope],
  );

  useEffect(() => {
    void loadBalances(1);
    // 筛选词变更经查询按钮显式触发，不进依赖（显式触发设计）
  }, []);

  // O-27：全局校区范围变化（切换器/持久化恢复）→ 立即按新口径重查（筛选词仍显式触发）
  const scopeRef = useRef(scope);
  useEffect(() => {
    if (scopeRef.current === scope) return;
    scopeRef.current = scope;
    void loadBalances(1);
  }, [scope, loadBalances]);

  const { columns, components } = useResizableColumns<EntitlementBalanceItem>([
    { title: '学生姓名', dataIndex: 'studentName' },
    { title: '学号', dataIndex: 'studentNo' },
    { title: 'SKU 名称', dataIndex: 'skuName' },
    {
      title: '购买剩余',
      dataIndex: 'purchasedRemaining',
      width: 110,
      render: (v, row) => (
        <a onClick={() => setHistoryContext(toContext(row))}>{v}</a>
      ),
    },
    {
      title: '赠送剩余',
      dataIndex: 'giftedRemaining',
      width: 110,
      render: (v, row) => (
        <a onClick={() => setHistoryContext(toContext(row))}>{v}</a>
      ),
    },
    {
      title: '可用剩余',
      dataIndex: 'availableTotal',
      width: 110,
      render: (v, row) => (
        <a onClick={() => setHistoryContext(toContext(row))}>{v}</a>
      ),
    },
    {
      title: '期限卡',
      dataIndex: 'periodPassSummary',
      width: 190,
      // 服务端契约：仅「生效中未到期」卡返回摘要文本（含到期日，三端同口径），
      // 待激活/已到期/无卡均为 null → 空态；状态色沿用 period-ledger 映射单一来源
      render: (value: string | null) =>
        value ? (
          <Tag color={periodPassStatusColor('ACTIVE')} style={{ marginInlineEnd: 0 }}>
            {value}
          </Tag>
        ) : (
          '—'
        ),
    },
  ]);

  const hasFilter = studentName.trim() !== '' || skuName.trim() !== '';

  return (
    <Card
      title="剩余课时查询"
      extra={
        <Space wrap>
          <Input
            allowClear
            placeholder="学生姓名"
            style={{ width: 180 }}
            value={studentName}
            onChange={(e) => setStudentName(e.target.value)}
            onPressEnter={() => void loadBalances(1)}
          />
          <Input
            allowClear
            placeholder="SKU 名称"
            style={{ width: 180 }}
            value={skuName}
            onChange={(e) => setSkuName(e.target.value)}
            onPressEnter={() => void loadBalances(1)}
          />
          <Button type="primary" onClick={() => void loadBalances(1)}>
            查询
          </Button>
          <Button
            onClick={() => {
              setStudentName('');
              setSkuName('');
              void loadBalances(1, { studentName: '', skuName: '' });
            }}
          >
            重置
          </Button>
        </Space>
      }
    >
      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        message="点击课时数字查看该学生该 SKU 的课时流水明细；课时余额由流水汇总生成，不提供修改入口。"
      />
      <Table<EntitlementBalanceItem>
        rowKey={(row) => `${row.studentId}:${row.skuId}`}
        loading={loading}
        columns={columns}
        components={components}
        scroll={{ x: 720 }}
        dataSource={rows}
        locale={{
          emptyText: hasFilter ? '未找到符合筛选条件的记录' : '暂无课时余额记录',
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: false,
          onChange: (target) => void loadBalances(target),
        }}
      />
      <BalanceHistoryModal
        context={historyContext}
        onClose={() => setHistoryContext(null)}
      />
    </Card>
  );
}

function toContext(row: EntitlementBalanceItem): BalanceHistoryContext {
  return {
    studentId: row.studentId,
    studentName: row.studentName,
    skuId: row.skuId,
    skuName: row.skuName,
  };
}
